import { forwardRef, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import { DATE_FORMATS } from '@callmart/types';
import { checkIsTableRowExpanded, getValueForMonthColumn } from '../../utils/agGridUtils';
import { numberValueFormatter, parseLocalizedNumber } from '../../utils/numberUtils';
import ButtonIconRenderer from '../AgGrid/ButtonIconRenderer/ButtonIconRenderer';
import { CapacityPlausibilityCheck } from '../../enums/CapacityPlausibilityCheck';
import TableGridItem from '../UI/TableGridItem';
import { CAPACITY_ANALYSIS_ROW_GROUP_LABEL_KEYS } from './helpers/constants';
import dayjs from '@utils/dayjs';

const getHeaderName = (date, locale) =>
    `${date.locale(locale).format(DATE_FORMATS.MONTH_SHORT)}\n${date.format(DATE_FORMATS.YEAR_TWO_DIGIT)}`;

const CapacityHorizontalScrollTable = forwardRef(
    (
        {
            firstMonth,
            onFirstMonthChanged,
            tableRows,
            expandedRows,
            onExpandedRowsChanged,
            getExpandedRowsInTable,
            tableType,
            calculateSum,
            getMonthColumns,
            ...props
        },
        ref
    ) => {
        const { t, i18n } = useTranslation();
        const [columnDefs, setColumnDefs] = useState([]);

        const goBackMonth = useCallback(
            (numOfMonths) => {
                onExpandedRowsChanged(getExpandedRowsInTable());

                const previousMonth = dayjs(firstMonth, DATE_FORMATS.ISO_DATE).subtract(numOfMonths, 'months');
                onFirstMonthChanged(previousMonth);
            },
            [firstMonth, getExpandedRowsInTable, onFirstMonthChanged, onExpandedRowsChanged]
        );

        const goForwardMonth = useCallback(
            (numOfMonths) => {
                onExpandedRowsChanged(getExpandedRowsInTable());

                const nextMonth = dayjs(firstMonth, DATE_FORMATS.ISO_DATE).add(numOfMonths, 'months');
                onFirstMonthChanged(nextMonth);
            },
            [firstMonth, getExpandedRowsInTable, onFirstMonthChanged, onExpandedRowsChanged]
        );

        const createColumnDefs = useCallback(
            (month) => {
                const monthColumns = getMonthColumns(month);
                const columns = [];
                const commonColumnDefs = [
                    {
                        field: 'worktype.name',
                        rowGroup: true,
                        hide: true,
                    },
                    {
                        field: 'channel.name',
                        rowGroup: true,
                        hide: true,
                    },
                    {
                        field: 'task.name',
                        rowGroup: true,
                        hide: true,
                    },
                    {
                        field: 'id',
                        headerName: t('field.capacity'),
                        valueGetter: (params) => `${params?.data?.capacityType.name ?? ''}`,
                        editable: false,
                        suppressMovable: true,
                        minWidth: 110,
                    },
                ];

                const columnDefsByTable = [];

                if (tableType === 'CAPACITY_ANALYSIS') {
                    columnDefsByTable.push(
                        {
                            field: 'rowsHeader',
                            rowGroup: true,
                            hide: true,
                            cellClass: 'empty-row',
                            // The stored value is a stable key (it feeds getRowId);
                            // translate only for display. '+/-' has no key.
                            valueFormatter: ({ value }) =>
                                CAPACITY_ANALYSIS_ROW_GROUP_LABEL_KEYS[value]
                                    ? t(CAPACITY_ANALYSIS_ROW_GROUP_LABEL_KEYS[value])
                                    : value,
                        },
                        ...commonColumnDefs
                    );

                    for (const monthColumn of monthColumns) {
                        columnDefsByTable.push({
                            field: `${monthColumn.format(DATE_FORMATS.YEAR_MONTH)}`,
                            headerName: getHeaderName(monthColumn, i18n.language),
                            valueGetter: (params) => getValueForMonthColumn(params, monthColumn),
                            valueFormatter: numberValueFormatter,
                            suppressMovable: true,
                            aggFunc: 'sum',
                        });
                    }
                } else if (tableType === 'CAPACITY_REPORT') {
                    columnDefsByTable.push(...commonColumnDefs, {
                        field: 'unit',
                        headerName: t('field.unit'),
                        suppressMovable: true,
                        cellRenderer: (params) => `${params?.data?.capacityType.unit ?? ''}`,
                        editable: false,
                        minWidth: 95,
                    });

                    for (const monthColumn of monthColumns) {
                        columnDefsByTable.push({
                            field: `${monthColumn.format(DATE_FORMATS.YEAR_MONTH)}`,
                            headerName: getHeaderName(monthColumn, i18n.language),
                            wrapText: true,
                            minWidth: 85,
                            cellRenderer: (params) => {
                                let textColor = {};
                                if (params?.data) {
                                    if (
                                        params?.data[`${monthColumn.format(DATE_FORMATS.YEAR_MONTH)}`]?.checked ===
                                        CapacityPlausibilityCheck.WRONG
                                    ) {
                                        textColor = { color: 'red' };
                                    } else if (
                                        params?.data[`${monthColumn.format(DATE_FORMATS.YEAR_MONTH)}`]?.checked ===
                                        CapacityPlausibilityCheck.CORRECT
                                    ) {
                                        textColor = { color: 'green' };
                                    }
                                }

                                return <span style={{ ...textColor }}>{params.valueFormatted}</span>;
                            },
                            valueGetter: (params) => getValueForMonthColumn(params, monthColumn),
                            valueSetter: (params) => {
                                const parsed = params.newValue === '' ? 0 : parseLocalizedNumber(params.newValue);
                                if (parsed === null && params.newValue !== '') {
                                    // Invalid input - reject the change
                                    return false;
                                }
                                params.data[`${monthColumn.format(DATE_FORMATS.YEAR_MONTH)}`].value = parsed;
                                return true;
                            },
                            valueFormatter: numberValueFormatter,
                            suppressMovable: true,
                            aggFunc: (params) => {
                                return calculateSum(params);
                            },
                        });
                    }
                }

                const defaultArrowColumnDefs = {
                    field: ``,
                    cellClass: 'grid-cell-centered',
                    headerComponent: ButtonIconRenderer,
                    maxWidth: 25,
                    editable: false,
                    sortable: false,
                    resizable: false,
                    filter: false,
                    suppressMovable: true,
                };

                columns.push(
                    {
                        ...defaultArrowColumnDefs,
                        headerComponent: ButtonIconRenderer,
                        headerComponentParams: {
                            icon: KeyboardDoubleArrowLeftIcon,
                            onClick: () => goBackMonth(12),
                            iconStyle: { width: '22px', height: '22px' },
                        },
                        pinned: 'left',
                        maxWidth: 30,
                    },
                    {
                        ...defaultArrowColumnDefs,
                        headerComponent: ButtonIconRenderer,
                        headerComponentParams: {
                            icon: ArrowBackIosIcon,
                            onClick: () => goBackMonth(1),
                            iconStyle: { width: '14px', height: '14px' },
                        },
                        pinned: 'left',
                    },
                    ...columnDefsByTable,
                    {
                        ...defaultArrowColumnDefs,

                        headerComponentParams: {
                            icon: ArrowForwardIosIcon,
                            onClick: () => goForwardMonth(1),
                            iconStyle: { width: '14px', height: '14px' },
                        },
                        pinned: 'right',
                    },
                    {
                        ...defaultArrowColumnDefs,
                        headerComponentParams: {
                            icon: KeyboardDoubleArrowRightIcon,
                            onClick: () => goForwardMonth(12),
                            iconStyle: { width: '22px', height: '22px' },
                        },
                        pinned: 'right',
                        maxWidth: 30,
                    }
                );

                setColumnDefs(columns);
            },
            [goBackMonth, goForwardMonth, getMonthColumns, tableType, calculateSum, t, i18n.language]
        );

        useEffect(() => {
            if (tableRows?.length && expandedRows.length) {
                ref.current.api.forEachNode((node) => {
                    const isTableRowExpanded = expandedRows.find((n) => checkIsTableRowExpanded(n, node));
                    if (isTableRowExpanded) {
                        node.setExpanded(true);
                    }
                });
            }
        }, [tableRows, expandedRows, ref]);

        useEffect(() => {
            if (firstMonth) {
                createColumnDefs(firstMonth);
            }
        }, [firstMonth, createColumnDefs]);

        const autoGroupColumnDef = useMemo(() => {
            return {
                minWidth: 230,
                headerName: '',
                resizable: true,
            };
        }, []);

        const getRowId = useMemo(() => (params) => `${params.data.rowsHeader}_${params.data.id}`, []);

        return (
            <TableGridItem
                ref={ref}
                columnDefs={columnDefs}
                animateRows={true}
                firstMonth={firstMonth}
                onFirstMonthChanged={onFirstMonthChanged}
                rowData={tableRows}
                expandedRows={expandedRows}
                onExpandedRowsChanged={onExpandedRowsChanged}
                getExpandedRowsInTable={getExpandedRowsInTable}
                autoGroupColumnDef={autoGroupColumnDef}
                suppressScrollOnNewData={true}
                getRowId={getRowId}
                {...props}
            ></TableGridItem>
        );
    }
);

export default memo(CapacityHorizontalScrollTable);
